import { User, UserPermissions, UserRole, DashboardConfig } from '../types/user.types'
import { logger } from './logger'

/**
 * Determina o papel do usuário baseado em suas características
 */
export const getUserRole = (user: User | null): UserRole => {
  if (!user) return 'attendant'
  
  logger.debug('🔍 getUserRole - Dados do usuário:', {
    id: user.id,
    name: user.name,
    email: user.email,
    status: user.status,
    type: user.type,
    hasActiveSubscription: user.hasActiveSubscription
  })
  
  if (user.status === 'super') return 'super_admin'
  if (user.type === 'admin' && user.hasActiveSubscription) return 'admin_with_subscription'
  if (user.type === 'user') return 'attendant'
  
  return 'attendant'
}

/**
 * Retorna as permissões do usuário baseado em seu papel
 */
export const getUserPermissions = (user: User | null): UserPermissions => {
  const role = getUserRole(user)
  
  switch (role) {
    case 'super_admin':
      return {
        canManageBots: true,
        canManageMenus: true,
        canManageTemplates: true,
        canViewConversations: true,
        canManageAttendants: true,
        canAccessFinancial: true,
        canAccessSettings: true,
        canCreateInvoicesForAdmins: true,
        canCreateInvoicesForClients: true,
        canManageAdmins: true,
        canManageSubscriptionPlans: true,
      }
    
    case 'admin_with_subscription':
      return {
        canManageBots: true,
        canManageMenus: true,
        canManageTemplates: true,
        canViewConversations: true,
        canManageAttendants: true,
        canAccessFinancial: true,
        canAccessSettings: true,
        canCreateInvoicesForAdmins: false, // Admin com assinatura não pode criar faturas para outros admins
        canCreateInvoicesForClients: true, // Apenas para clientes (cobranças de serviços)
        canManageAdmins: false,
        canManageSubscriptionPlans: false,
      }
    
    case 'attendant':
      return {
        canManageBots: false,
        canManageMenus: false,
        canManageTemplates: false,
        canViewConversations: true,
        canManageAttendants: false,
        canAccessFinancial: true,
        canAccessSettings: false,
        canCreateInvoicesForAdmins: false,
        canCreateInvoicesForClients: true, // Atendentes podem criar faturas para clientes
        canManageAdmins: false,
        canManageSubscriptionPlans: false,
      }
    
    default:
      return {
        canManageBots: false,
        canManageMenus: false,
        canManageTemplates: false,
        canViewConversations: false,
        canManageAttendants: false,
        canAccessFinancial: false,
        canAccessSettings: false,
        canCreateInvoicesForAdmins: false,
        canCreateInvoicesForClients: false,
        canManageAdmins: false,
        canManageSubscriptionPlans: false,
      }
  }
}

/**
 * Retorna as rotas permitidas para o usuário
 */
export const getAllowedRoutes = (user: User | null): string[] => {
  const permissions = getUserPermissions(user)
  const routes: string[] = ['/dashboard']
  
  if (permissions.canManageBots) {
    routes.push('/bots', '/bots/create', '/bots/:id', '/bots/:id/edit')
  }
  
  if (permissions.canManageMenus) {
    routes.push('/menus', '/menus/create', '/menus/:id/edit')
  }
  
  if (permissions.canManageTemplates) {
    routes.push('/templates', '/templates/create', '/templates/management')
  }
  
  if (permissions.canViewConversations) {
    routes.push('/conversations', '/conversations/:id', '/quick-replies', '/clients')
  }
  
  if (permissions.canManageAttendants) {
    routes.push('/users', '/user-management')
  }
  
  if (permissions.canAccessFinancial) {
    routes.push('/financial')
    if (permissions.canCreateInvoicesForAdmins || permissions.canCreateInvoicesForClients) {
      routes.push('/invoices/create', '/invoices/:id', '/invoices/:id/payment')
    }
  }
  
  if (permissions.canAccessSettings) {
    routes.push('/settings')
  }
  
  if (permissions.canManageAdmins) {
    routes.push('/admins', '/admin-management')
  }
  
  if (permissions.canManageSubscriptionPlans) {
    routes.push('/subscription-plans')
  }
  
  if (permissions.canManageBots) {
    routes.push('/bot-messages')
  }
  
  return routes
}

/**
 * Verifica se o usuário tem permissão para acessar uma rota
 */
export const hasRoutePermission = (user: User | null, route: string): boolean => {
  const allowedRoutes = getAllowedRoutes(user)
  return allowedRoutes.some(allowedRoute => {
    // Converter rota com parâmetros para regex
    const regex = new RegExp(allowedRoute.replace(/:\w+/g, '[^/]+'))
    return regex.test(route)
  })
}

/**
 * Retorna a configuração do dashboard baseado no papel do usuário
 */
export const getDashboardConfig = (user: User | null): DashboardConfig => {
  const role = getUserRole(user)
  getUserPermissions(user) // Mantém a chamada para evitar warning
  
  switch (role) {
    case 'super_admin':
      return {
        role,
        title: 'Dashboard do Super Administrador',
        description: 'Gerencie todo o sistema, administradores e assinaturas',
        quickActions: [
          {
            id: 'create-bot',
            title: 'Criar Novo Bot',
            description: 'Configure um novo chatbot',
            icon: 'Bot',
            path: '/bots/create',
            color: 'blue',
            requiredPermission: 'canManageBots'
          },
          {
            id: 'manage-menus',
            title: 'Criar Menus',
            description: 'Configure menus e submenus',
            icon: 'MessageSquare',
            path: '/menus/create',
            color: 'green',
            requiredPermission: 'canManageMenus'
          },
          {
            id: 'manage-templates',
            title: 'Criar Templates',
            description: 'Crie templates personalizados',
            icon: 'Settings',
            path: '/templates',
            color: 'orange',
            requiredPermission: 'canManageTemplates'
          },
          {
            id: 'view-conversations',
            title: 'Ver Conversas',
            description: 'Acompanhe as conversas ativas',
            icon: 'MessageSquare',
            path: '/conversations',
            color: 'purple',
            requiredPermission: 'canViewConversations'
          },
          {
            id: 'manage-admins',
            title: 'Gerenciar Admins',
            description: 'Gerencie administradores e assinaturas',
            icon: 'Crown',
            path: '/admin-management',
            color: 'yellow',
            requiredPermission: 'canManageAdmins'
          },
          {
            id: 'bot-messages',
            title: 'Mensagens do Bot',
            description: 'Personalize mensagens automáticas',
            icon: 'MessageSquare',
            path: '/bot-messages',
            color: 'indigo',
            requiredPermission: 'canManageBots'
          },
          {
            id: 'subscription-plans',
            title: 'Planos de Assinatura',
            description: 'Configure planos e limites',
            icon: 'Settings',
            path: '/subscription-plans',
            color: 'indigo',
            requiredPermission: 'canManageSubscriptionPlans'
          },
          {
            id: 'manage-attendants',
            title: 'Atendentes',
            description: 'Gerencie usuários de atendimento',
            icon: 'Users',
            path: '/user-management',
            color: 'green',
            requiredPermission: 'canManageAttendants'
          },
          {
            id: 'financial',
            title: 'Financeiro',
            description: 'Gerencie faturas e pagamentos',
            icon: 'DollarSign',
            path: '/financial',
            color: 'green',
            requiredPermission: 'canAccessFinancial'
          },
          {
            id: 'settings',
            title: 'Configurações',
            description: 'Gerencie suas configurações',
            icon: 'Settings',
            path: '/settings',
            color: 'gray',
            requiredPermission: 'canAccessSettings'
          }
        ],
        statsCards: [
          {
            id: 'bots',
            title: 'Bots Ativos',
            value: 0,
            subtitle: 'total',
            icon: 'Bot',
            color: 'blue'
          },
          {
            id: 'conversations',
            title: 'Conversas Hoje',
            value: 0,
            subtitle: 'aguardando',
            icon: 'MessageSquare',
            color: 'green'
          },
          {
            id: 'clients',
            title: 'Clientes',
            value: 0,
            subtitle: 'cadastrados no sistema',
            icon: 'Users',
            color: 'purple'
          },
          {
            id: 'response-rate',
            title: 'Taxa de Resposta',
            value: '0%',
            subtitle: 'respostas automáticas hoje',
            icon: 'TrendingUp',
            color: 'orange'
          }
        ],
        allowedRoutes: getAllowedRoutes(user)
      }
    
    case 'admin_with_subscription':
      return {
        role,
        title: 'Dashboard do Administrador',
        description: 'Gerencie seus bots, conversas e atendentes',
        quickActions: [
          {
            id: 'create-bot',
            title: 'Criar Novo Bot',
            description: 'Configure um novo chatbot',
            icon: 'Bot',
            path: '/bots/create',
            color: 'blue',
            requiredPermission: 'canManageBots'
          },
          {
            id: 'manage-menus',
            title: 'Criar Menus',
            description: 'Configure menus e submenus',
            icon: 'MessageSquare',
            path: '/menus/create',
            color: 'green',
            requiredPermission: 'canManageMenus'
          },
          {
            id: 'manage-templates',
            title: 'Criar Templates',
            description: 'Crie templates personalizados',
            icon: 'Settings',
            path: '/templates',
            color: 'orange',
            requiredPermission: 'canManageTemplates'
          },
          {
            id: 'view-conversations',
            title: 'Ver Conversas',
            description: 'Acompanhe as conversas ativas',
            icon: 'MessageSquare',
            path: '/conversations',
            color: 'purple',
            requiredPermission: 'canViewConversations'
          },
          {
            id: 'bot-messages',
            title: 'Mensagens do Bot',
            description: 'Personalize mensagens automáticas',
            icon: 'MessageSquare',
            path: '/bot-messages',
            color: 'indigo',
            requiredPermission: 'canManageBots'
          },
          {
            id: 'manage-attendants',
            title: 'Atendentes',
            description: 'Gerencie usuários de atendimento',
            icon: 'Users',
            path: '/user-management',
            color: 'green',
            requiredPermission: 'canManageAttendants'
          },
          {
            id: 'financial',
            title: 'Financeiro',
            description: 'Gerencie faturas e pagamentos',
            icon: 'DollarSign',
            path: '/financial',
            color: 'green',
            requiredPermission: 'canAccessFinancial'
          },
          {
            id: 'settings',
            title: 'Configurações',
            description: 'Gerencie suas configurações',
            icon: 'Settings',
            path: '/settings',
            color: 'gray',
            requiredPermission: 'canAccessSettings'
          }
        ],
        statsCards: [
          {
            id: 'bots',
            title: 'Bots Ativos',
            value: 0,
            subtitle: 'total',
            icon: 'Bot',
            color: 'blue'
          },
          {
            id: 'conversations',
            title: 'Conversas Hoje',
            value: 0,
            subtitle: 'aguardando',
            icon: 'MessageSquare',
            color: 'green'
          },
          {
            id: 'clients',
            title: 'Clientes',
            value: 0,
            subtitle: 'cadastrados no sistema',
            icon: 'Users',
            color: 'purple'
          },
          {
            id: 'response-rate',
            title: 'Taxa de Resposta',
            value: '0%',
            subtitle: 'respostas automáticas hoje',
            icon: 'TrendingUp',
            color: 'orange'
          }
        ],
        allowedRoutes: getAllowedRoutes(user)
      }
    
    case 'attendant':
      return {
        role,
        title: 'Dashboard do Atendente',
        description: 'Visualize conversas e dados financeiros',
        quickActions: [
          {
            id: 'view-conversations',
            title: 'Ver Conversas',
            description: 'Acompanhe as conversas ativas',
            icon: 'MessageSquare',
            path: '/conversations',
            color: 'purple',
            requiredPermission: 'canViewConversations'
          },
          {
            id: 'financial',
            title: 'Financeiro',
            description: 'Visualize dados financeiros',
            icon: 'DollarSign',
            path: '/financial',
            color: 'green',
            requiredPermission: 'canAccessFinancial'
          }
        ],
        statsCards: [
          {
            id: 'active-conversations',
            title: 'Conversas Ativas',
            value: 0,
            subtitle: 'aguardando sua atenção',
            icon: 'MessageSquare',
            color: 'blue'
          },
          {
            id: 'conversations-today',
            title: 'Conversas Hoje',
            value: 0,
            subtitle: 'atendidas hoje',
            icon: 'MessageSquare',
            color: 'green'
          },
          {
            id: 'avg-response-time',
            title: 'Tempo Médio de Resposta',
            value: '0 min',
            subtitle: 'hoje',
            icon: 'Clock',
            color: 'yellow'
          },
          {
            id: 'invoices-generated',
            title: 'Faturas Geradas',
            value: 0,
            subtitle: 'este mês',
            icon: 'DollarSign',
            color: 'purple'
          }
        ],
        allowedRoutes: getAllowedRoutes(user)
      }
    
    default:
      return {
        role: 'attendant',
        title: 'Dashboard',
        description: 'Acesso limitado',
        quickActions: [],
        statsCards: [],
        allowedRoutes: ['/dashboard']
      }
  }
}